"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _reactDom = _interopRequireDefault(require("react-dom"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactJss = _interopRequireDefault(require("react-jss"));
var _utils = _interopRequireDefault(require("../utils/utils"));
var _hooks = require("../hooks");
var _constants = require("./constants");
var _utils2 = require("./utils");
var _PopoverContent = _interopRequireDefault(require("./PopoverContent.style"));
const _excluded = ["onClose", "anchors", "topLevel", "triggers", "bindingElement", "boundaryElement", "padding", "classes", "children", "className", "style", "verticalPlacingOnly", "fitToBindingElementWidth", "clickPosition", "dataTestId"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Renders a popover element that can be opened by multiple triggers and displays content
 * when opened. The content can be positioned relative to the triggering element.
 */
const StyledPopoverContent = (0, _reactJss.default)(_PopoverContent.default)(_ref => {
  let {
      onClose,
      anchors: anchorsProp,
      topLevel,
      triggers,
      bindingElement,
      boundaryElement: boundaryElementProp,
      padding,
      classes,
      children,
      className,
      style,
      verticalPlacingOnly,
      fitToBindingElementWidth,
      clickPosition,
      dataTestId
    } = _ref,
    props = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const [state, setState] = (0, _hooks.useObjectState)({
    opened: false,
    anchors: anchorsProp,
    cordinates: {
      x: 0,
      y: 0
    },
    paddingSides: {},
    width: 'auto'
  });
  const boundaryElement = boundaryElementProp || document.body;
  const contentElementRef = (0, _react.useRef)(null);
  const anchorsRef = (0, _react.useRef)(state.anchors);
  const cordinatesRef = (0, _react.useRef)(state.cordinates);
  const handleClickOutside = (0, _react.useCallback)((e, ...args) => {
    // Extra check to ignore repeated clicks on binding element
    if (bindingElement && !bindingElement.contains(e.target)) {
      onClose(e, ...args);
    }
  }, [bindingElement, onClose]);
  (0, _hooks.useOutside)({
    ref: contentElementRef,
    callback: handleClickOutside,
    triggers
  });
  const updatePosition = (0, _react.useCallback)(() => {
    var _cordinatesRef$curren, _cordinatesRef$curren2;
    if (!contentElementRef.current) return;
    const {
      anchors,
      cordinates
    } = (0, _utils2.getPopoverPosition)({
      bindingElement,
      boundaryElement,
      contentElement: contentElementRef.current,
      desiredAnchors: anchorsProp,
      lastAnchors: anchorsRef.current,
      verticalPlacingOnly,
      fitToBindingElementWidth,
      coords: clickPosition
    });
    const cordinatesUpdated = cordinates && ((cordinates === null || cordinates === void 0 ? void 0 : cordinates.x) !== ((_cordinatesRef$curren = cordinatesRef.current) === null || _cordinatesRef$curren === void 0 ? void 0 : _cordinatesRef$curren.x) || (cordinates === null || cordinates === void 0 ? void 0 : cordinates.y) !== ((_cordinatesRef$curren2 = cordinatesRef.current) === null || _cordinatesRef$curren2 === void 0 ? void 0 : _cordinatesRef$curren2.y));
    if (cordinatesUpdated) {
      setState.cordinates(cordinates);
      cordinatesRef.current = cordinates;
      if (anchors) {
        setState.anchors(anchors);
        anchorsRef.current = anchors;
      }
    }
  }, []);

  // useLayoutEffect used to set correct position before painting to avoid popover flickering
  (0, _react.useLayoutEffect)(() => {
    if (fitToBindingElementWidth) {
      setState.width(bindingElement.getBoundingClientRect().width);
    }
    if (!boundaryElement) return;
    updatePosition();
    const updateAnchorsWithDebounce = _utils.default.throttle(updatePosition, 10);
    const resizeObserver = new ResizeObserver(updateAnchorsWithDebounce);
    resizeObserver.observe(contentElementRef.current);
    resizeObserver.observe(bindingElement);
    resizeObserver.observe(boundaryElement);
    boundaryElement.addEventListener('scroll', updateAnchorsWithDebounce, topLevel);
    return () => {
      resizeObserver.disconnect();
      boundaryElement.removeEventListener('scroll', updateAnchorsWithDebounce);
    };
  }, []);
  (0, _react.useLayoutEffect)(() => {
    if (!clickPosition) return;
    updatePosition();
  }, [clickPosition]);
  const paddingStyles = (0, _react.useMemo)(() => (0, _utils2.getPaddingStyles)(state.anchors, padding), [state.anchors, padding]);
  return topLevel && boundaryElement ? _reactDom.default.createPortal(/*#__PURE__*/_react.default.createElement("div", (0, _extends2.default)({
    ref: contentElementRef,
    style: _objectSpread({
      display: 'inline-block',
      padding: paddingStyles,
      left: `${state.cordinates.x}px`,
      top: `${state.cordinates.y}px`,
      width: state.width
    }, style),
    className: (0, _clsx.default)(className, classes.popoverContent, 'popoverContent'),
    "data-testid": dataTestId
  }, props), children), boundaryElement) : /*#__PURE__*/_react.default.createElement("div", (0, _extends2.default)({
    ref: contentElementRef,
    style: _objectSpread({
      display: 'inline-block',
      padding: paddingStyles,
      width: state.width
    }, style),
    className: (0, _clsx.default)(className, classes.popoverContent, 'popoverContent', `bindingAnchor_${state.anchors.binding}`, `contentAnchor_${state.anchors.content}`),
    "data-testid": dataTestId
  }, props), children);
});
const PopoverContent = props => /*#__PURE__*/_react.default.createElement(StyledPopoverContent, props);
PopoverContent.defaultProps = {
  anchors: {
    binding: _constants.ANCHOR.center_bottom,
    content: _constants.ANCHOR.center_top
  },
  style: {}
};
var _default = exports.default = PopoverContent;